import { useState, useEffect, useCallback } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { toast } from "sonner";
import axios from "axios";
import { User, UserPlus, Mail, Trash2, Save, X, Clock } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const Users = () => {
  const [user, setUser] = useState(null);
  const [members, setMembers] = useState([]);
  const [invites, setInvites] = useState([]);
  const [inviteEmail, setInviteEmail] = useState("");
  const [editingName, setEditingName] = useState(false);
  const [firstNameValue, setFirstNameValue] = useState("");
  const [lastNameValue, setLastNameValue] = useState("");
  const [loading, setLoading] = useState(true);
  const [inviting, setInviting] = useState(false);

  const splitLegacyName = (full) => {
    const trimmed = (full || "").trim();
    if (!trimmed) return { first: "", last: "" };
    const idx = trimmed.lastIndexOf(" ");
    if (idx === -1) return { first: trimmed, last: "" };
    return { first: trimmed.slice(0, idx).trim(), last: trimmed.slice(idx + 1).trim() };
  };

  const fetchData = useCallback(async () => {
    try {
      const [meRes, teamRes] = await Promise.all([
        axios.get(`${API}/auth/me`),
        axios.get(`${API}/team/members`),
      ]);
      setUser(meRes.data);
      const first = meRes.data.first_name;
      const last = meRes.data.last_name;
      if (first || last) {
        setFirstNameValue(first || "");
        setLastNameValue(last || "");
      } else {
        const parts = splitLegacyName(meRes.data.name);
        setFirstNameValue(parts.first);
        setLastNameValue(parts.last);
      }
      setMembers(teamRes.data.members);
      setInvites(teamRes.data.invites);
    } catch {
      toast.error("Gegevens laden mislukt");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleSaveName = async () => {
    const first = firstNameValue.trim();
    const last = lastNameValue.trim();
    if (!first && !last) {
      toast.error("Vul minimaal je voornaam of achternaam in");
      return;
    }
    try {
      const res = await axios.put(`${API}/auth/profile`, { first_name: first, last_name: last });
      setUser(res.data);
      setEditingName(false);
      toast.success("Naam bijgewerkt");
    } catch {
      toast.error("Naam bijwerken mislukt");
    }
  };

  const handleInvite = async (e) => {
    e.preventDefault();
    if (!inviteEmail.trim()) return;
    setInviting(true);
    try {
      const res = await axios.post(`${API}/team/invite`, { email: inviteEmail.trim().toLowerCase() });
      toast.success(res.data.message);
      setInviteEmail("");
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Uitnodiging mislukt");
    } finally {
      setInviting(false);
    }
  };

  const handleCancelInvite = async (email) => {
    try {
      await axios.delete(`${API}/team/invite/${encodeURIComponent(email)}`);
      toast.success("Uitnodiging geannuleerd");
      fetchData();
    } catch {
      toast.error("Annuleren mislukt");
    }
  };

  const handleRemoveMember = async (memberId) => {
    try {
      await axios.delete(`${API}/team/members/${memberId}`);
      toast.success("Manager verwijderd");
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Verwijderen mislukt");
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-6 h-6 border-2 border-slate-300 border-t-slate-900 rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div data-testid="users-page" className="max-w-2xl mx-auto space-y-8">
      {/* Header */}
      <div>
        <h1
          className="text-4xl md:text-5xl font-bold tracking-tight uppercase text-slate-900"
          style={{ fontFamily: "var(--font-display)" }}
        >
          Gebruikers
        </h1>
        <p className="text-slate-500 mt-1">Beheer je account en teammanagers</p>
      </div>

      {/* My Account */}
      <Card className="border-slate-200 shadow-sm rounded-xl" data-testid="my-account-card">
        <CardHeader className="pb-3">
          <CardTitle className="text-base font-semibold flex items-center gap-2">
            <User className="w-4 h-4" />
            Mijn account
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center gap-4">
            {user?.picture ? (
              <img src={user.picture} alt="" className="w-12 h-12 rounded-full" />
            ) : (
              <div className="w-12 h-12 rounded-full bg-slate-200 flex items-center justify-center">
                <User className="w-6 h-6 text-slate-500" />
              </div>
            )}
            <div className="flex-1">
              {editingName ? (
                <div className="flex flex-wrap items-center gap-2" data-testid="edit-name-fields">
                  <Input
                    value={firstNameValue}
                    onChange={(e) => setFirstNameValue(e.target.value)}
                    placeholder="Voornaam"
                    className="h-8 text-sm w-40"
                    autoFocus
                    data-testid="edit-first-name-input"
                    onKeyDown={(e) => e.key === "Enter" && handleSaveName()}
                  />
                  <Input
                    value={lastNameValue}
                    onChange={(e) => setLastNameValue(e.target.value)}
                    placeholder="Achternaam"
                    className="h-8 text-sm w-40"
                    data-testid="edit-last-name-input"
                    onKeyDown={(e) => e.key === "Enter" && handleSaveName()}
                  />
                  <Button size="sm" variant="ghost" onClick={handleSaveName} data-testid="save-name-btn">
                    <Save className="w-3.5 h-3.5" />
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => {
                      setEditingName(false);
                      if (user?.first_name || user?.last_name) {
                        setFirstNameValue(user?.first_name || "");
                        setLastNameValue(user?.last_name || "");
                      } else {
                        const parts = splitLegacyName(user?.name);
                        setFirstNameValue(parts.first);
                        setLastNameValue(parts.last);
                      }
                    }}
                  >
                    <X className="w-3.5 h-3.5" />
                  </Button>
                </div>
              ) : (
                <div className="flex items-center gap-2">
                  <p className="font-medium text-slate-900">{user?.name}</p>
                  <button
                    className="text-xs text-slate-400 hover:text-slate-600"
                    onClick={() => setEditingName(true)}
                    data-testid="edit-name-trigger"
                  >
                    bewerken
                  </button>
                </div>
              )}
              <p className="text-sm text-slate-500">{user?.email}</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Team Managers */}
      <Card className="border-slate-200 shadow-sm rounded-xl" data-testid="team-managers-card">
        <CardHeader className="pb-3">
          <CardTitle className="text-base font-semibold flex items-center gap-2">
            <UserPlus className="w-4 h-4" />
            Teammanagers
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Current Members */}
          <div className="space-y-2">
            {members.map((m) => (
              <div
                key={m.user_id}
                className="flex items-center justify-between py-2 px-3 rounded-lg bg-slate-50"
                data-testid={`member-${m.user_id}`}
              >
                <div className="flex items-center gap-3">
                  {m.picture ? (
                    <img src={m.picture} alt="" className="w-8 h-8 rounded-full" />
                  ) : (
                    <div className="w-8 h-8 rounded-full bg-slate-200 flex items-center justify-center">
                      <User className="w-4 h-4 text-slate-500" />
                    </div>
                  )}
                  <div>
                    {m.user_id === user?.user_id && editingName ? (
                      <div className="flex flex-wrap items-center gap-2">
                        <Input
                          value={firstNameValue}
                          onChange={(e) => setFirstNameValue(e.target.value)}
                          placeholder="Voornaam"
                          className="h-7 text-sm w-32"
                          autoFocus
                          data-testid="member-edit-first-name-input"
                          onKeyDown={(e) => e.key === "Enter" && handleSaveName()}
                        />
                        <Input
                          value={lastNameValue}
                          onChange={(e) => setLastNameValue(e.target.value)}
                          placeholder="Achternaam"
                          className="h-7 text-sm w-32"
                          data-testid="member-edit-last-name-input"
                          onKeyDown={(e) => e.key === "Enter" && handleSaveName()}
                        />
                        <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={handleSaveName} data-testid="member-save-name-btn">
                          <Save className="w-3.5 h-3.5" />
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          className="h-7 w-7 p-0"
                          onClick={() => {
                            setEditingName(false);
                            if (user?.first_name || user?.last_name) {
                              setFirstNameValue(user?.first_name || "");
                              setLastNameValue(user?.last_name || "");
                            } else {
                              const parts = splitLegacyName(user?.name);
                              setFirstNameValue(parts.first);
                              setLastNameValue(parts.last);
                            }
                          }}
                        >
                          <X className="w-3.5 h-3.5" />
                        </Button>
                      </div>
                    ) : (
                      <div className="flex items-center gap-2">
                        <p className="text-sm font-medium text-slate-900">{m.name}</p>
                        {m.user_id === user?.user_id && (
                          <button
                            className="text-xs text-slate-400 hover:text-slate-600"
                            onClick={() => setEditingName(true)}
                            data-testid="member-edit-name-trigger"
                          >
                            bewerken
                          </button>
                        )}
                      </div>
                    )}
                    <p className="text-xs text-slate-500">{m.email}</p>
                  </div>
                </div>
                {m.user_id !== user?.user_id && (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="text-slate-400 hover:text-red-600 hover:bg-red-50"
                    onClick={() => handleRemoveMember(m.user_id)}
                    data-testid={`remove-member-${m.user_id}`}
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </Button>
                )}
              </div>
            ))}
          </div>

          {/* Pending Invites */}
          {invites.length > 0 && (
            <div className="space-y-2">
              <p className="text-xs font-medium uppercase tracking-wider text-slate-500">Openstaande uitnodigingen</p>
              {invites.map((inv) => (
                <div
                  key={inv.email}
                  className="flex items-center justify-between py-2 px-3 rounded-lg bg-amber-50 border border-amber-100"
                  data-testid={`invite-${inv.email}`}
                >
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full bg-amber-100 flex items-center justify-center">
                      <Clock className="w-4 h-4 text-amber-600" />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-slate-900">{inv.email}</p>
                      <p className="text-xs text-amber-600">Wacht op registratie</p>
                    </div>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="text-slate-400 hover:text-red-600 hover:bg-red-50"
                    onClick={() => handleCancelInvite(inv.email)}
                    data-testid={`cancel-invite-${inv.email}`}
                  >
                    <X className="w-3.5 h-3.5" />
                  </Button>
                </div>
              ))}
            </div>
          )}

          {/* Invite Form */}
          <form onSubmit={handleInvite} className="flex items-end gap-2 pt-2 border-t border-slate-100" data-testid="invite-form">
            <div className="flex-1 space-y-1">
              <Label className="text-xs text-slate-500">Manager uitnodigen</Label>
              <Input
                type="email"
                placeholder="e-mailadres"
                value={inviteEmail}
                onChange={(e) => setInviteEmail(e.target.value)}
                className="h-9 text-sm"
                required
                data-testid="invite-email-input"
              />
            </div>
            <Button
              type="submit"
              size="sm"
              className="h-9 bg-slate-900 hover:bg-slate-800"
              disabled={inviting}
              data-testid="invite-submit-btn"
            >
              <Mail className="w-3.5 h-3.5 mr-1" />
              {inviting ? "Bezig..." : "Uitnodigen"}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};
